import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ChevronRight, X, RefreshCw } from 'lucide-react';
import healthApi from '@/api/healthApi';
import { toast } from 'sonner';
import analyticsService from '@/utils/analyticsService';
import { useLanguage } from '@/contexts/LanguageContext';

const DailyHealthInsight = ({ onClose, embedded = false }) => {
  const { t } = useLanguage();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchInsight();
  }, []);

  const fetchInsight = async () => {
    try {
      const { data } = await healthApi.getDailyHealthInsight();
      setInsight(data);
      
      // Track insight view
      analyticsService.trackInsightInteraction('insight_viewed', {
        category: data.category,
        priority: data.priority
      });
    } catch (error) {
      console.error('Error fetching daily health insight:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/daily-health-insight/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setInsight(data);
      
      // Track refresh
      analyticsService.trackInsightInteraction('insight_refreshed', {
        old_category: insight?.category,
        new_category: data.category
      });
      
      toast.success(t('dailyInsight.newInsightLoaded'));
    } catch (error) {
      console.error('Error refreshing insight:', error);
      toast.error(t('dailyInsight.failedToRefresh'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onClose) {
      onClose();
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 animate-pulse">
        <CardContent className="p-6">
          <div className="h-24"></div>
        </CardContent>
      </Card>
    );
  }

  if (dismissed || !insight) {
    return null;
  }

  const getCategoryColor = (category) => {
    const colors = {
      sleep: 'from-purple-100 to-indigo-100 border-purple-300',
      stress: 'from-blue-100 to-cyan-100 border-blue-300',
      hydration: 'from-blue-100 to-teal-100 border-blue-300',
      activity: 'from-green-100 to-emerald-100 border-green-300',
      mood: 'from-purple-100 to-pink-100 border-purple-300',
      energy: 'from-green-100 to-lime-100 border-green-300',
      nutrition: 'from-orange-100 to-yellow-100 border-orange-300',
      welcome: 'from-indigo-100 to-purple-100 border-indigo-300'
    };
    return colors[category] || colors.welcome;
  };

  const getPriorityIndicator = (priority) => {
    if (priority === 'high') {
      return <span className="inline-block px-2 py-0.5 bg-purple-500 text-white text-xs font-bold rounded">{t('dailyInsight.priority.high')}</span>;
    } else if (priority === 'medium') {
      return <span className="inline-block px-2 py-0.5 bg-blue-500 text-white text-xs font-bold rounded">{t('dailyInsight.priority.medium')}</span>;
    }
    return null;
  };

  return (
    <Card className={`border-2 bg-gradient-to-br ${getCategoryColor(insight.category)} shadow-lg ${embedded ? '' : 'animate-fadeIn'}`}>
      <CardContent className="p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                  💡 {t('dailyInsight.title')}
                </h3>
                {getPriorityIndicator(insight.priority)}
              </div>
              <p className="text-xs text-gray-600 capitalize">
                {insight.category === 'welcome' ? t('dailyInsight.gettingStarted') : `${insight.category} ${t('dailyInsight.focus')}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex-shrink-0 hover:bg-white/50"
              title={t('dailyInsight.getNewInsight')}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            {!embedded && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="flex-shrink-0 hover:bg-white/50"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Icon */}
        <div className="text-center mb-4">
          <span className="text-5xl sm:text-6xl">{insight.icon}</span>
        </div>

        {/* Structured Insight Section */}
        <div className="space-y-4">
          {/* Relatable Fact */}
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
            <p className="text-xs font-bold text-green-900 mb-2 uppercase tracking-wide flex items-center gap-2">
              💡 {t('dailyInsight.quickInsight')}
            </p>
            <p className="text-base sm:text-lg font-semibold text-gray-900 leading-relaxed">
              {insight.relatable_fact}
            </p>
          </div>

          {/* Explanation */}
          <div className="p-4 bg-white/90 rounded-xl border border-purple-200">
            <p className="text-xs font-semibold text-purple-900 mb-2 uppercase tracking-wide">
              📚 {t('dailyInsight.simpleExplanation')}
            </p>
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
              {insight.explanation}
            </p>
          </div>

          {/* Real-Life Impact */}
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
            <p className="text-xs font-semibold text-blue-900 mb-2 uppercase tracking-wide">
              🎯 {t('dailyInsight.realLifeImpact')}
            </p>
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
              {insight.impact}
            </p>
          </div>

          {/* Personalized Action */}
          <div className="p-5 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl border-2 border-purple-300 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <ChevronRight className="h-6 w-6 text-purple-700" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-purple-900 mb-1 uppercase tracking-wide">
                  ✨ {t('dailyInsight.yourActionStep')}
                </p>
                <p className="text-xs text-purple-700 mb-2 italic">
                  {t('dailyInsight.basedOnYour')} {insight.personalization_context}
                </p>
                <p className="text-sm sm:text-base text-gray-900 font-semibold leading-relaxed">
                  👉 {insight.action}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-xs text-gray-600 text-center mt-4 italic">
          {t('dailyInsight.personalizedFor')}
        </p>
      </CardContent>
    </Card>
  );
};

export default DailyHealthInsight;
